/**
 * src/RpgPage.js
 *
 * VISINVOKE RPG Dashboard - application dashboard only.
 * Aligned to the Combat PRO dashboard design language: dark application theme,
 * ownership / purchase style world panels with application info rows and stat
 * tiles instead of marketing copy. CYBERTECH and VERIDIAN link to their existing
 * guarded checkout surfaces; launch state remains distinct from entitlement.
 *
 * All field values are placeholders ( -  / "Not Started" / "In Development").
 * Nothing here implies a working backend.
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProductNav from './components/ProductNav';
import MoreInfoPill from './components/MoreInfoPill';
import { glassSurface } from './config/glassTheme';

const RPG_MOTION_CSS = `
  @keyframes rpg-fade-up {
    from { opacity: 0; transform: translateY(28px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .rpg-reveal {
    opacity: 0;
    transform: translateY(28px);
    transition: opacity 440ms cubic-bezier(0.22,1,0.36,1),
                transform 440ms cubic-bezier(0.22,1,0.36,1);
  }
  .rpg-reveal.rpg-visible {
    opacity: 1;
    transform: translateY(0);
  }
  .rpg-reveal-delay-1 { transition-delay: 70ms; }
  .rpg-reveal-delay-2 { transition-delay: 140ms; }

  .rpg-world-card {
    transition: transform 240ms cubic-bezier(0.22,1,0.36,1),
                box-shadow 240ms ease,
                border-color 240ms ease !important;
  }

  /* Responsive stacking. Desktop columns stay defined inline (so the two-up
     layout paints immediately with no flash); these media rules override to a
     single column on narrow widths. !important is required to win over the
     inline grid-template-columns. */
  @media (max-width: 820px) {
    .rpg-world-grid { grid-template-columns: 1fr !important; gap: 22px !important; }
  }
  @media (max-width: 640px) {
    .rpg-price-grid { grid-template-columns: 1fr !important; gap: 16px !important; }
  }
`;

// Placeholder glyph (escaped so no literal U+2014 is in the source file).
const DASH = ' - ';

// -- World definitions --------------------------------------------------------

const WORLDS = [
  {
    key: 'veridian',
    label: 'Fantasy World',
    name: 'Veridian',
    icon: '\u2694\uFE0F',
    accent: '#E53B63',
    accent2: '#8E1937',
    glow: 'rgba(229,59,99,0.20)',
    gDeep: '#8E1937',
    gBright: '#FF5A7E',
    artGrad: 'linear-gradient(135deg, #1B0C10 0%, #8E1937 50%, #4c1d3d 100%)',
    price: '$14.99',
    btn: 'View and purchase Veridian',
    stats: [
      { label: 'Characters', value: DASH },
      { label: 'Settlements', value: DASH },
    ],
    rows: [
      ['Purchase Status', 'Available'],
      ['Current Version', DASH],
      ['Save Status', DASH],
      ['Cloud Sync', DASH],
      ['Last Played', DASH],
      ['Progress Status', 'Not Started'],
    ],
  },
  {
    key: 'cybertech',
    label: 'Science-Fiction World',
    name: 'CyberTech',
    icon: '\uD83E\uDD16',
    accent: '#2563eb',
    accent2: '#06b6d4',
    glow: 'rgba(37,99,235,0.20)',
    gDeep: '#123A8A',
    gBright: '#5CC8FF',
    artGrad: 'linear-gradient(135deg, #0c1a2e 0%, #134e6f 50%, #1e293b 100%)',
    price: '$14.99',
    btn: 'View and purchase CyberTech',
    stats: [
      { label: 'Characters', value: DASH },
      { label: 'Cities', value: DASH },
    ],
    rows: [
      ['Purchase Status', 'Available'],
      ['Current Version', DASH],
      ['Save Status', DASH],
      ['Cloud Sync', DASH],
      ['Last Played', DASH],
      ['Progress Status', 'Not Started'],
    ],
  },
];

// -- World application card ---------------------------------------------------

function WorldCard({ world, delay }) {
  const navigate = useNavigate();
  return (
    <div
      className={`rpg-world-card rpg-reveal ${delay}`}
      style={pg.worldCard}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 24px 60px rgba(0,0,0,0.45), 0 0 0 1px ${world.accent}40`; e.currentTarget.style.borderColor = `${world.accent}55`; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 14px 44px rgba(0,0,0,0.35)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
    >
      {/* Artwork strip - reduced height for application focus */}
      <div style={{ ...pg.worldArt, background: world.artGrad }}>
        <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at 50% 40%, ${world.glow} 0%, transparent 70%)`, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${world.accent}, transparent)` }} />
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 14, zIndex: 1 }}>
          <div style={{ fontSize: 34, filter: 'drop-shadow(0 4px 14px rgba(0,0,0,0.45))' }}>{world.icon}</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}>{world.name} - Key Artwork</div>
        </div>
      </div>

      {/* Body */}
      <div style={pg.worldBody}>

        {/* Eyebrow */}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: world.accent, marginBottom: 12 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: world.accent }} />
          {world.label}
        </div>

        {/* Title + status badge + price. Price sits directly under the status
            badge (right-aligned) so it reads immediately at title level without
            competing with the world name. */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 22 }}>
          <h2 style={pg.worldName}>{world.name}</h2>
          <div style={pg.titleRightCol}>
            <span style={pg.statusBadge}>Available to purchase</span>
            <div style={pg.priceTag}>
              <span style={pg.priceAmount}>{world.price}</span>
              <span style={pg.pricePer}>/mo</span>
            </div>
          </div>
        </div>

        {/* Stat tiles */}
        <div style={pg.statGrid}>
          {world.stats.map((s, i) => (
            <div key={i} style={pg.statTile}>
              <div style={pg.statValue}>{s.value}</div>
              <div style={pg.statLabel}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Application info rows */}
        <div style={pg.rowList}>
          {world.rows.map((r, i) => (
            <div key={i} style={{ ...pg.row, borderBottom: i < world.rows.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
              <span style={pg.rowLabel}>{r[0]}</span>
              <span style={pg.rowValue}>{r[1]}</span>
            </div>
          ))}
        </div>

        {/* World-specific purchase entry point. Account state and consent are
            verified on the destination before Stripe opens. */}
        <button
          type="button"
          style={{ ...pg.worldBtn, ...glassSurface(world.gDeep, world.accent, world.gBright), cursor: 'pointer' }}
          onClick={() => navigate(`/dashboard/rpg/${world.key}`)}
        >
          <span>{world.btn}</span>
          <span style={pg.comingSoonPill}>US$14.99 / month</span>
        </button>

        {/* Opens this world's uniform Dashboard detail page. Not a launch or
            purchase control. */}
        <div style={pg.moreInfoWrap}>
          <MoreInfoPill productKey={world.key} accent={world.accent} />
        </div>
      </div>
    </div>
  );
}

// -- Page ---------------------------------------------------------------------

export default function RpgPage() {
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'rpg-motion-css';
    if (!document.getElementById('rpg-motion-css')) document.head.appendChild(style);
    style.textContent = RPG_MOTION_CSS;

    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('rpg-visible'); observer.unobserve(e.target); } }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.rpg-reveal').forEach(el => observer.observe(el));
    return () => { observer.disconnect(); };
  }, []);

  return (
    <div style={pg.page}>
      <ProductNav />

      {/* ============================ STANDARD VISINVOKE PRODUCT HEADER */}
      <section style={pg.headerSection}>
        <div className="vi-desktop-wide" style={pg.container}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <h1 style={pg.headerTitle}>VISINVOKE RPG Worlds</h1>
          </div>
          <p style={pg.headerSub}>Persistent solo RPG worlds built for years of progression.</p>
        </div>
      </section>

      {/* =================================================== WORLD CARDS */}
      <section style={pg.cardsSection}>
        <div className="vi-desktop-wide" style={pg.container}>
          <div className="rpg-world-grid" style={pg.worldGrid}>
            <WorldCard world={WORLDS[0]} delay="rpg-reveal-delay-1" />
            <WorldCard world={WORLDS[1]} delay="rpg-reveal-delay-2" />
          </div>

          {/* ========================================= RPG WORLDS PRICING */}
          <PricingSection />
        </div>
      </section>
    </div>
  );
}

// -- RPG Worlds pricing section ----------------------------------------------
// Full-width native dashboard section beneath the two world cards. It presents
// the approved fixed prices ($14.99 / world, $24.95 for both). The "Both Worlds"
// option carries restrained extra
// prominence (a blended purple->blue accent line + glow relating to both worlds)
// because it is the better-value package.

const PRICE_ONE  = '$14.99';
const PRICE_BOTH = '$24.95';

function PricingSection() {
  return (
    <div style={pg.pricingWrap} className="rpg-reveal">
      <div style={pg.pricingHeadingWrap}>
        <h2 style={pg.pricingHeading}>RPG WORLDS PRICING</h2>
        <p style={pg.pricingSub}>
          Choose one world, or access both Veridian and CyberTech together under a single RPG Worlds subscription.
        </p>
      </div>

      <div className="rpg-price-grid" style={pg.priceGrid}>
        {/* ONE WORLD */}
        <div style={pg.priceCard}>
          <div style={pg.priceEyebrow}>ONE WORLD</div>
          <div style={pg.priceBigRow}>
            <span style={pg.priceBig}>{PRICE_ONE}</span>
            <span style={pg.priceBigPer}>/mo</span>
          </div>
          <p style={pg.priceDesc}>Choose either Veridian or CyberTech.</p>
        </div>

        {/* BOTH WORLDS - better value, restrained prominence */}
        <div style={pg.priceCardBoth}>
          <div style={pg.priceBothTopLine} />
          <span style={pg.priceBestChip}>Best Value</span>
          <div style={pg.priceEyebrowBoth}>BOTH WORLDS</div>
          <div style={pg.priceBigRow}>
            <span style={pg.priceBigBoth}>{PRICE_BOTH}</span>
            <span style={pg.priceBigPer}>/mo</span>
          </div>
          <p style={pg.priceDesc}>Access Veridian and CyberTech together under one RPG Worlds subscription.</p>
        </div>
      </div>
    </div>
  );
}

// -- Styles (Combat PRO application design language) --------------------------

const pg = {
  page: {
    minHeight: '100vh',
    background: 'radial-gradient(1200px 640px at 50% -4%, rgba(229,59,99,0.09) 0%, rgba(0,0,0,0) 44%), radial-gradient(1000px 720px at 100% 112%, rgba(229,59,99,0.05) 0%, rgba(0,0,0,0) 55%), #080B11',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: '#C7CFDC',
  },

  container: { maxWidth: 1280, margin: '0 auto', padding: '0 40px' },

  /* -- Header -- */
  headerSection: { padding: '44px 0 10px' },
  headerTitle: { fontSize: 52, fontWeight: 900, color: '#ffffff', lineHeight: 1.1, margin: 0, letterSpacing: '-1px' },
  headerSub: { fontSize: 18, fontWeight: 500, color: '#C7CFDC', lineHeight: 1.5, margin: '12px 0 0', textAlign: 'center' },

  /* -- Cards section -- */
  cardsSection: { padding: '32px 0 96px' },
  worldGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 },

  /* -- World application panel -- */
  worldCard: {
    background: '#291218',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 18,
    overflow: 'hidden',
    boxShadow: '0 14px 44px rgba(0,0,0,0.35)',
    display: 'flex',
    flexDirection: 'column',
  },
  worldArt: {
    position: 'relative',
    height: 120,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    padding: '0 28px',
  },
  worldBody: { padding: '26px 28px 28px', display: 'flex', flexDirection: 'column', flex: 1 },
  worldName: { fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 30, fontWeight: 900, color: '#ffffff', margin: 0, lineHeight: 1 },

  /* -- Status badge (analogue of Combat PRO "Owned") -- */
  statusBadge: {
    ...glassSurface('#A66D05', '#F59E0B', '#FBBF24'),
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 12,
    fontWeight: 700,
    borderRadius: 100,
    padding: '6px 14px',
    whiteSpace: 'nowrap',
  },

  /* -- Per-card price (sits under the status badge, right-aligned) -- */
  titleRightCol: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 8,
  },
  priceTag: { display: 'flex', alignItems: 'baseline', gap: 4 },
  priceAmount: { fontSize: 20, fontWeight: 800, color: '#ffffff', letterSpacing: '-0.3px', lineHeight: 1 },
  pricePer: { fontSize: 12, fontWeight: 600, color: '#8F9AAC' },

  /* -- Stat tiles -- */
  statGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 22 },
  statTile: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 12,
    padding: '18px 16px',
    textAlign: 'center',
  },
  statValue: { fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 900, color: '#ffffff', lineHeight: 1, marginBottom: 8 },
  statLabel: { fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8F9AAC' },

  /* -- Info rows -- */
  rowList: { marginBottom: 24 },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 0' },
  rowLabel: { fontSize: 14, color: '#8F9AAC' },
  rowValue: { fontSize: 14, color: '#C7CFDC', fontWeight: 600 },

  moreInfoWrap: { marginTop: 12 },

  /* -- Purchase entry button -- */
  worldBtn: {
    marginTop: 'auto',
    width: '100%',
    border: 'none',
    borderRadius: 12,
    padding: '15px 22px',
    fontSize: 15,
    fontWeight: 800,
    letterSpacing: '0.01em',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  worldBtnDisabled: {
    background: 'rgba(255,255,255,0.05)',
    color: '#cbd5e1',
    border: '1px solid rgba(255,255,255,0.12)',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  comingSoonPill: {
    ...glassSurface('#A66D05', '#F59E0B', '#FBBF24'),
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    borderRadius: 100,
    padding: '4px 11px',
    lineHeight: 1,
  },

  /* -- RPG Worlds pricing section (native dashboard styling) -- */
  pricingWrap: { marginTop: 48 },
  pricingHeadingWrap: { textAlign: 'center', marginBottom: 26 },
  pricingHeading: {
    fontSize: 24,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '0.14em',
    margin: 0,
    lineHeight: 1.2,
  },
  pricingSub: {
    fontSize: 15,
    fontWeight: 500,
    color: '#C7CFDC',
    lineHeight: 1.55,
    margin: '10px auto 0',
    maxWidth: 640,
  },

  priceGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'stretch' },

  priceCard: {
    position: 'relative',
    overflow: 'hidden',
    minWidth: 0,
    background: '#291218',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 18,
    padding: '26px 26px 28px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 14px 44px rgba(0,0,0,0.35)',
  },
  priceCardBoth: {
    position: 'relative',
    overflow: 'hidden',
    minWidth: 0,
    background: '#291218',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 18,
    padding: '30px 26px 28px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 0 0 1px rgba(229,59,99,0.28), 0 18px 50px rgba(229,59,99,0.16), 0 14px 44px rgba(0,0,0,0.4)',
  },
  priceBothTopLine: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: 'linear-gradient(90deg, #E53B63, #2563eb)',
    pointerEvents: 'none',
  },
  priceBestChip: {
    position: 'absolute',
    top: 16,
    right: 16,
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#ffffff',
    background: 'linear-gradient(90deg, rgba(229,59,99,0.9), rgba(37,99,235,0.9))',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: 100,
    padding: '4px 10px',
    lineHeight: 1,
  },
  priceEyebrow: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: '#8F9AAC',
    marginBottom: 14,
  },
  priceEyebrowBoth: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: '#FF8AA2',
    marginBottom: 14,
  },
  priceBigRow: { display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 12 },
  priceBig: { fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 34, fontWeight: 900, color: '#ffffff', lineHeight: 1 },
  priceBigBoth: {
    fontFamily: 'Georgia, "Times New Roman", serif',
    fontSize: 34,
    fontWeight: 900,
    lineHeight: 1,
    background: 'linear-gradient(90deg, #FF5A7E, #60a5fa)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    color: '#FF8AA2',
  },
  priceBigPer: { fontSize: 14, fontWeight: 600, color: '#8F9AAC' },
  priceDesc: { fontSize: 14, color: '#8F9AAC', lineHeight: 1.6, margin: 0 },
};
